/**
 * DOM anchors for Amplitude Guides & Surveys pins, selected as
 * `[data-analytics-id="<value>"]`. Separate from `data-testid`, which the tests
 * own and change freely.
 *
 * Bind at the call site, so the attribute falls through onto the component
 * root. Bind inside a component only for an element the caller cannot reach.
 *
 * Keep values literal, never interpolated, or the guards cannot see them.
 * Register every group in `ANALYTICS_TARGET_GROUPS`.
 *
 * No patient or clinical data. Renaming a published value orphans its pin:
 * add and deprecate instead.
 */

/** The application shell, on every authenticated route. */
export const APP_TARGETS = {
    notificationIcon: 'guide-notification-icon',
    helpIcon: 'guide-help-icon',
    profileMenuIcon: 'guide-profile-menu-icon',
    caseMenu: 'guide-case-menu',
    caseTitle: 'guide-case-title',
    formusLogo: 'guide-formus-logo',
} as const;

export const PLANNER_TARGETS = {
    plannerMeasurements: 'guide-planner-measurements',

    optimisationTargets: 'guide-optimisation-targets',
    stemContainer: 'guide-stem-container',
    stemTranslationButtons: 'guide-stem-translation-btns',
    stemRotationButtons: 'guide-stem-rotation-btns',
    stemResetButtonGroup: 'guide-stem-reset-btn-group',
    stemResetButton: 'guide-stem-reset-btn',

    cupSizeAdjust: 'guide-cup-size-adjust',
    cupAdjustments: 'guide-cup-adjustments',
    cupTranslation: 'guide-cup-translation',
    cupTranslationMode: 'guide-cup-translation-mode',
    cupResetButton: 'guide-cup-reset-btn',

    generatePlanButton: 'guide-generate-plan-btn',

    viewControlsBar: 'guide-view-controls-bar',
    viewModeIcons: 'guide-view-mode-icons',
    // Three cross-section toggles share the bar, so each anchor names its own.
    neckCrossSectionButton: 'guide-neck-cross-section-btn',
    stemCrossSectionButton: 'guide-stem-cross-section-btn',
    snapToViewButtons: 'guide-snap-view-btns',
    ctButton: 'guide-ct-btn',
} as const;

export const HIP_SPINE_TARGETS = {
    hipSpineButton: 'guide-hipspine-btn',
    hipSpineToggle: 'guide-hipspine-toggle',
    anteversionSlider: 'guide-anteversion-slider',
    inclinationSlider: 'guide-inclination-slider',
    safeZoneToggle: 'guide-safe-zone-toggle',
    hipSpineWarnings: 'guide-hipspine-warnings',
    hipSpineAnalysis: 'guide-hipspine-analysis',
    boundsControls: 'guide-bounds-controls',
    nomogram: 'guide-nomogram',

    xRayTabs: 'guide-x-ray-tabs',
    xRayImageControls: 'guide-x-ray-image-controls',
    measurementsTabGroup: 'guide-measurements-tab-group',
    anglesTab: 'guide-angles-tab',
    annotationsTab: 'guide-annotations-tab',
    approveButtonGroup: 'guide-approve-btn-group',
} as const;

export const PREFERENCES_TARGETS = {
    stemNotice: 'guide-stem-notice',
    hipSpineIntro: 'guide-hipspine-intro',
    hipSpineSaveButton: 'guide-hipspine-save-btn',
    surgicalGuideCard: 'guide-surgical-guide-card',
    cupInclinationAngle: 'guide-cup-inclination-angle',
} as const;

export const CASE_SETTINGS_TARGETS = {
    newCaseButton: 'guide-new-case-btn',
    surgeryDate: 'guide-surgery-date',
    implantSide: 'guide-implant-side',
    patientInfoIntro: 'guide-patient-info-intro',
    familyName: 'guide-family-name',
    patientSex: 'guide-patient-sex',
    patientDob: 'guide-patient-dob',
    selectSurgeon: 'guide-select-surgeon',
    crNumberInput: 'guide-cr-number-input',
    legLengthInput: 'guide-leg-length-input',
    saveCaseButton: 'guide-save-case-btn',
    uploadSection: 'guide-upload-section',
} as const;

/** Figma's "Report Page". */
export const PLANS_TARGETS = {
    downloadReportButton: 'guide-download-report-btn',
    psiButton: 'guide-psi-btn',
    rawButton: 'guide-raw-btn',
    orderModalButtons: 'guide-order-modal-btn-group',
} as const;

/** The picker renders on both the preferences page and the case page. */
export const STEM_SYSTEM_OPTION_TARGETS: Record<string, string> = {
    'avenir-complete': 'guide-avenir-complete-option',
    'taperloc-complete': 'guide-taperloc-option',
    'ms-30': 'guide-ms30-option',
    z1: 'guide-z1-option',
};

/** Vue drops an `undefined` binding, so an unlisted system gets no anchor rather than another's. */
export function stemSystemOptionTarget(system: string): string | undefined {
    return STEM_SYSTEM_OPTION_TARGETS[system];
}

export const PLANNER_SIDEBAR_TARGETS = {
    stem: 'guide-stem-menu-icon',
    cup: 'guide-cup-menu-icon',
} as const;

export const NATIVE_MEASUREMENT_TARGETS = {
    'hip-length-difference': 'guide-hld',
} as const;

export function nativeMeasurementTarget(measurement: string): string | undefined {
    return NATIVE_MEASUREMENT_TARGETS[measurement as keyof typeof NATIVE_MEASUREMENT_TARGETS];
}

export type AnalyticsTargetGroup = Readonly<Record<string, string>>;

/** The guards derive from this map: a group left out is checked by nothing. */
export const ANALYTICS_TARGET_GROUPS: Readonly<Record<string, AnalyticsTargetGroup>> = {
    APP_TARGETS,
    PLANNER_TARGETS,
    HIP_SPINE_TARGETS,
    PREFERENCES_TARGETS,
    CASE_SETTINGS_TARGETS,
    PLANS_TARGETS,
    STEM_SYSTEM_OPTION_TARGETS,
    PLANNER_SIDEBAR_TARGETS,
    NATIVE_MEASUREMENT_TARGETS,
};
